/**
 * A class to hold a value as a promise.  The idea being that if the reference
 * to this class is maintained, then the it should be possible to call set and
 * get in any order and wait for a response (as a promise).
 */
class AsyncEntry {
  constructor(value) {
    if (value) {
      this.promise = Promise.resolve(value);
    } else {
      this.resolve = null;
      this.promise = new Promise((resolve) => {
        this.resolve = resolve;
      });
    }
  }

  set value(value) {
    if (this.resolve) {
      this.resolve(value);
      delete this.resolve;
    } else {
      this.promise = Promise.resolve(value);
    }
  }

  get value() {
    return this.promise;
  }
}

/**
 * This is simply a map, but it will return a promise to a
 * a value rather than the value itself.  This ensures that
 * get requests that occur before a value is set will wait
 * until the value has been set.
 */
class AsyncMap {
  constructor() {
    this.map = new Map();
  }

  getRaw(key) {
    if (!this.map.has(key)) {
      const entry = new AsyncEntry();
      this.map.set(key, entry);
    }
    return this.map.get(key);
  }

  async set(key, value) {
    this.getRaw(key).value = value;
  }

  async get(key) {
    return this.getRaw(key).value;
  }
}

/**
 * A map of subscriptions.  This should be the only way the review
 * screens interact with a live plugin.
 */
export default class SubscriptionStore {
  constructor(rootInstance) {
    this.rootInstance = rootInstance;
    this.subscriptions = new AsyncMap();

    this.addSubscription = this.addSubscription.bind(this);
    this.notify = this.notify.bind(this);
    this.constructKey = this.constructKey.bind(this);
  }

  constructKey(id, resourceName, methodName) {
    const _id = id || this.rootInstance;
    return `${_id}-${methodName}-${resourceName}`;
  }

  addSubscription({ id, resourceName, methodName, notifier }) {
    this.subscriptions.set(this.constructKey(id, resourceName, methodName), notifier);
  }

  find({ resourceName, methodName, id }) {
    return this.subscriptions.get(this.constructKey(id, resourceName, methodName));
  }

  /**
   * Notifies a service with a value.  If the service is not
   * registerd it will wait until it is.
   */
  async notify({ resourceName, methodName, payload, id }) {
    const notifier = await this.find({ id, resourceName, methodName });
    return notifier(payload);
  }
}
